import type { Plural } from '../../core.js';
import { report } from './report.js';

const p = (forms: Plural): Plural => forms;

/** Texts of the domain model (catalogs, warnings, plan labels, report). Part of `de`. */
export const model = {
  report,
  libraryCategories: {
    people: 'Personen',
    vehicles: 'Fahrzeuge',
    storage: 'Lager',
    machines: 'Maschinen',
    office: 'Büro',
    social: 'Sozialraum & Sanitär',
    outdoor: 'Außenbereich',
    misc: 'Sonstiges',
  },
  libraryGroups: {
    sweptPaths: 'Schleppkurven',
    racks: 'Regale',
    loadCarriers: 'Ladungsträger & Lagergut',
    cabinets: 'Schränke',
    shipping: 'Versand',
    handling: 'Flurförderung',
    waste: 'Entsorgung',
    machining: 'Bearbeitung',
    welding: 'Schweißen',
    cranes: 'Krane',
    conveying: 'Fördertechnik',
    workshop: 'Werkstatt',
    utilities: 'Haustechnik',
    break: 'Pause',
    changing: 'Umkleide',
    sanitary: 'Sanitär',
  },
  library: {
    person: 'Person',
    car: 'PKW',
    van: 'Transporter',
    truck: 'LKW 7,5 t',
    'semi-trailer': 'Sattelzug',
    'swept-car': 'Schleppkurve Pkw',
    'swept-truck': 'Schleppkurve Lkw 7,5 t',
    'swept-semi': 'Schleppkurve Sattelzug',
    forklift: 'Gabelstapler',
    pallet: 'Europalette',
    'pallet-rack-1825': 'Palettenregal 1.825 mm',
    'pallet-rack': 'Palettenregal 2.700 mm',
    'pallet-rack-3600': 'Palettenregal 3.600 mm',
    'pallet-rack-5560': 'Palettenregal 5.560 mm (LTT Spezial)',
    shelf: 'Fachbodenregal 3,00 m',
    'shelf-2500': 'Fachbodenregal 2,50 m',
    'shelf-2000': 'Fachbodenregal 2,00 m',
    'cantilever-rack': 'Kragarmregal einseitig',
    'cantilever-rack-double': 'Kragarmregal doppelseitig',
    'container-20': 'Container 20 ft',
    machine: 'Maschine',
    'cnc-machine': 'CNC-Maschine',
    'welding-table-1x1': 'Schweißtisch 1 × 1 m',
    'welding-table-2x1': 'Schweißtisch 2 × 1 m',
    'welding-station': 'Schweißarbeitsplatz (Kabine)',
    'truss-welding-table': 'Traversenschweißtisch',
    workbench: 'Werkbank',
    'control-cabinet': 'Schaltschrank',
    desk: 'Bürotisch',
    tank: 'Tank',
    'underfloor-saw': 'Unterflur-Kreissäge mit Rollbahnen',
    'cnc-router': 'CNC-Holzfräse (Portal, 3 × 2 m)',
    'jib-crane': 'Säulenschwenkkran',
    'bridge-crane': 'Brückenkran (Laufkran)',
    'press-brake': 'Abkantpresse',
    'band-saw': 'Metall-Bandsäge',
    'drill-press': 'Säulenbohrmaschine',
    compressor: 'Druckluft-Kompressor mit Kessel',
    'dust-extractor': 'Absauganlage mit Filter',
    'mesh-box': 'Gitterbox',
    'pallet-truck': 'Hubwagen (Ameise)',
    'flow-rack': 'Durchlaufregal',
    'klt-rack': 'Kleinteileregal mit KLT-Behältern',
    'packing-table': 'Packtisch',
    'floor-scale': 'Bodenwaage',
    'hazmat-cabinet': 'Gefahrstoffschrank',
    'charging-station': 'Stapler-Ladestation',
    'press-container': 'Presscontainer',
    'bench-desk': 'Doppelarbeitsplatz (Bench)',
    'corner-desk': 'Winkelschreibtisch',
    'office-chair': 'Bürostuhl',
    'file-cabinet': 'Aktenschrank',
    'drawer-pedestal': 'Rollcontainer',
    'meeting-table': 'Besprechungstisch (6 Plätze)',
    'meeting-table-4': 'Besprechungstisch (4 Plätze)',
    'server-rack': 'Serverschrank 19″ (42 HE)',
    'plotter-a0': 'Großformatplotter A0',
    'copy-station': 'Kopierstation (Kopieren und Scannen)',
    'table-group-4': 'Vierer-Sitzgruppe',
    'table-group-8': 'Langtisch-Gruppe (8 Plätze, kombinierbar)',
    kitchenette: 'Küchenzeile',
    fridge: 'Kühlschrank',
    'locker-2': 'Spind 2-türig',
    'locker-3': 'Spind 3-türig',
    'locker-4': 'Spind 4-türig',
    'changing-bench': 'Umkleidebank',
    shower: 'Duschkabine',
    'wc-cubicle': 'WC-Kabine',
    'wash-trough': 'Waschrinne (3 Plätze)',
    urinal: 'Urinal',
    wc: 'WC (freistehend)',
    'washbasin-60': 'Waschtisch mit Spiegel 60 cm',
    'gas-tank': 'Schutzgastank (Argon/CO₂)',
    'site-toilet': 'Mobile Bautoilette',
    'bike-shelter': 'Fahrradüberdachung',
    'ev-charger': 'E-Ladesäule',
    'waste-station': 'Müllsammelplatz',
    gatehouse: 'Pförtnerhaus mit Schranke',
    'skip-wood-7': 'Absetzcontainer Holz 7 m³',
    'skip-waste-10': 'Absetzcontainer Restmüll 10 m³',
    'waste-bin-1100': 'Müllgroßbehälter 1.100 l',
    'tilting-skip': 'Stapler-Kippbehälter',
    'recycling-station': 'Wertstoff-Trennstation (3-fach)',
    'shipping-table': 'Versandarbeitstisch mit Abrollvorrichtung',
    'alu-tube-bundle': 'Aluminiumrohrbund 6 m',
    'pallet-stack-15': 'Europalettenstapel (15 Stück)',
    'steel-cabinet': 'Werkstatt-Flügeltürschrank',
    'heavy-rack': 'Schwerlast-Fachbodenregal',
    'wall-jib-crane': 'Wandschwenkkran (4 m Auslage)',
    'work-table': 'Arbeitstisch 3,0 × 1,2 m',
    'twin-head-welder': 'Zweikopf-Schweißmaschine 7 m',
    'truss-welding-table-partition': 'Traversenschweißtisch mit Trennwand',
    'roller-conveyor': 'Rollenbahn 3 m',
    'tool-wall': 'Werkzeuglochwand (Wandmontage)',
    'tool-trolley': 'Werkstattwagen',
    'desk-plain': 'Bürotisch (ohne PC)',
    sideboard: 'Sideboard',
    'fire-extinguisher': 'Feuerlöscher mit Schild',
    'first-aid-station': 'Erste-Hilfe-Station',
    'emergency-shower': 'Notdusche mit Augendusche',
    'welding-screen': 'Schweißschutz-Trennwand 3 × 2 m',
    'pallet-boxes-1x1': 'Palette mit Kartons 1 × 1 m (16 Stück)',
    'truss-carton-pallet': 'Palette mit Traversenkartons (28 Stück)',
    'pallet-carton-1x1': 'Palette mit Kartonage 1 × 1 m',
    'pallet-carton-2x1': 'Palette mit Kartonage 2 × 1 m',
  },
  openings: {
    'sectional-door': 'Sektionaltor',
    'rolling-door': 'Industrie-Rolltor',
    'rapid-door': 'Schnelllauftor',
    'fire-gate': 'Brandschutz-Schiebetor',
    door: 'Tür',
    'double-door': 'Doppeltür',
    'glass-door': 'Glastür',
    'glass-double-door': 'Glas-Doppeltür',
    passage: 'Wanddurchbruch',
    'double-passage': 'Wanddurchbruch breit',
    window: 'Fenster',
    'window-band': 'Fensterband',
    'loading-dock': 'LKW-Laderampe',
    void: 'Wandöffnung (offen)',
  },
  zoneCategories: {
    production: 'Produktion',
    storage: 'Lager',
    'goods-in': 'Wareneingang',
    'goods-out': 'Warenausgang',
    assembly: 'Montage',
    packaging: 'Verpackung',
    office: 'Büro',
    social: 'Sozialraum',
    technical: 'Technik',
    workshop: 'Werkstatt',
    'open-space': 'Freifläche',
    'truck-access': 'LKW-Zufahrt',
    'forklift-path': 'Staplerweg',
    walkway: 'Fußweg',
    'escape-route': 'Rettungsweg',
    'work-area': 'Arbeitsbereich',
    paving: 'Pflasterfläche',
    green: 'Grünfläche',
    gravel: 'Kiesfläche',
    'crushed-stone': 'Schotterfläche',
    asphalt: 'Asphaltfläche',
    concrete: 'Betonfläche',
    other: 'Sonstiges',
  },
  zoneGroups: {
    function: 'Funktionsflächen',
    traffic: 'Verkehrsflächen',
    surface: 'Beläge und Grünflächen',
  },
  fenceKinds: {
    mesh: 'Doppelstabmatte',
    mobile: 'Bauzaun (mobil)',
  },
  wallTypes: {
    drywall: 'Trockenbau 12,5 cm',
    'masonry-115': 'Kalksandstein 11,5 cm',
    'masonry-175': 'Kalksandstein 17,5 cm',
    'masonry-24': 'Kalksandstein 24 cm',
    'concrete-20': 'Beton 20 cm',
    'concrete-25': 'Beton 25 cm',
    'brick-24': 'Klinker 24 cm',
    sandwich: 'Iso-Paneel 15 cm (senkrecht)',
    'sandwich-horizontal': 'Iso-Paneel 15 cm (waagerecht)',
  },
  roomTypes: {
    office: 'Büro',
    meeting: 'Besprechung',
    kitchen: 'Teeküche / Pause',
    'wc-women': 'WC Damen',
    'wc-men': 'WC Herren',
    'wc-accessible': 'WC barrierefrei',
    changing: 'Umkleide',
    shower: 'Dusche',
    corridor: 'Flur',
    technical: 'Technik',
    storage: 'Lager / Archiv',
    other: 'Raum',
  },
  roomTemplates: {
    'corridor-offices': {
      label: 'Flur mit Büros',
      hint: 'Flur 1,50 m längs, Büros von ca. 3,60 m Breite beidseitig (bei schmalen Gebäuden einseitig).',
    },
    social: {
      label: 'Sozialtrakt',
      hint: 'Flur 1,50 m längs, dazu WC Damen/Herren/barrierefrei, Umkleiden, Dusche und Teeküche.',
    },
  },
  roads: {
    road: 'Straße',
    'truck-access': 'LKW-Zufahrt',
    'fire-lane': 'Feuerwehrzufahrt',
    footpath: 'Gehweg',
  },
  parkings: {
    car: 'PKW-Parkplatz',
    truck: 'LKW-Stellplätze',
  },
  roofElements: {
    'skylight-dome': 'Lichtkuppel',
    'skylight-band': 'Lichtband',
    'smoke-vent': 'RWA-Lichtkuppel',
  },
  stairTypes: {
    straight: 'Gerade Treppe',
    u: 'Zweiläufige Treppe (U)',
    l: 'Winkeltreppe (L)',
  },
  sides: {
    south: 'Südwand',
    east: 'Ostwand',
    north: 'Nordwand',
    west: 'Westwand',
  },
  levels: {
    ground: 'EG',
    upper: '{n}. OG',
    top: '{n}. OG (Empore)',
  },
  sectionName: 'Bereich {letter}',
  warnings: {
    overplanned: 'In {name} sind mehr Flächen verplant als vorhanden',
    officeOutsideHall: '{name} ist als Einbau geplant, steht aber nicht vollständig in einer Halle',
    officeAboveEave: '{name} ({height}) ist höher als die Traufe von {hall} ({eave})',
    officeLowAir: '{name} ({height}) lässt unter der Traufe von {hall} nur {air} Luft',
    stair: '{name}: {hint}',
    buildingOutsideSite: '{name} liegt nicht vollständig auf dem Grundstück',
    sitesOverlap: 'Grundstücke überschneiden sich – die Fläche würde doppelt gezählt',
    overlap: '{a} und {b} überschneiden sich',
  },
  dock: {
    steep: 'Rampe mit {pct} % steiler als 10 % – Auflieger kann an die Wand stoßen',
    narrow: 'Andockzufahrt nur {width} m breit (mindestens {min} m)',
    short: 'Standfläche der Laderampe nur {length} m lang (mindestens {min} m)',
    narrowerThanGate: 'Andockzufahrt ist schmaler als das Tor',
    levelerWider: 'Überladebrücke ist breiter als das Tor',
    offSite: 'Andockzufahrt ragt über das Grundstück hinaus',
    overlap: 'Zwei Andockzufahrten überschneiden sich – Tore weiter auseinander setzen',
  },
  costs: {
    title: 'Mengen und Kostenschätzung',
    columns: ['Position', 'Menge', 'Einheitspreis', 'Betrag'],
    units: { t: 't', m2: 'm²', m3: 'm³', pcs: 'Stk', kWp: 'kWp' },
    perUnit: { t: '€/t', m2: '€/m²', m3: '€/m³', pcs: '€/Stk', kWp: '€/kWp' },
    items: {
      steel: 'Stahlbau (Tragwerk, montiert)',
      concrete: 'Stahlbeton: Beton C30/37 (eingebaut)',
      rebar: 'Stahlbeton: Betonstahl (verlegt)',
      formwork: 'Stahlbeton: Schalung',
      precast: 'Betonfertigteile (Stützen, Binder, Unterzüge, montiert)',
      roofSandwich: 'Dach Sandwich-Paneel',
      roofFlat: 'Flachdach (Trapezblech, Dämmung, Abdichtung)',
      wallSandwich: 'Wand Sandwich-Paneel',
      wallTrapezoid: 'Wand Trapezblech',
      wallMasonry: 'Mauerwerk (Kalksandstein, verputzt)',
      wallBrick: 'Mauerwerk Klinker',
      wallDrywall: 'Trockenbauwand',
      hallFloor: 'Bodenplatte / Industrieboden',
      mezzanine: 'Bühne (Stahl)',
      gate: 'Sektional- / Rolltor',
      fireGate: 'Brandschutz-Schiebetor',
      rapidDoor: 'Schnelllauftor',
      dock: 'Verladetor mit Überladebrücke',
      door: 'Tür',
      doubleDoor: 'Doppeltür',
      window: 'Fenster / Lichtband (Wand)',
      skylight: 'Lichtkuppel',
      skylightBand: 'Lichtband (Dach)',
      smokeVent: 'Rauch- und Wärmeabzug (RWA)',
      pv: 'PV-Anlage auf dem Dach (Module, Unterkonstruktion, Wechselrichter, montiert)',
    },
    surcharges: {
      foundations: 'Fundamente und Erdarbeiten',
      services: 'Haustechnik',
      fees: 'Baunebenkosten (Planung, Gebühren)',
    },
    measured: 'Summe ermittelte Positionen',
    surcharge: '{label} ({percent} %)',
    total: 'Gesamt (netto)',
    range: 'Spanne (± {percent} %)',
    notesTitle: 'Annahmen und Grenzen',
    steelDetail:
      'Stahl: Rahmen {frame} t, Pfetten/Riegel {secondary} t, Verbände {bracing} t, Stützen {columns} t, Anschlüsse {extra} t',
    concreteDetail:
      'Stahlbeton: Decken {slab} m³, Unterzüge {beam} m³, Stützen {column} m³, Wände {wall} m³, Fundamente {foundation} m³; Bewehrung {rebar} t (Richtwerte je m³)',
    priceBasis: 'Preisbasis: Katalog „{name}“, ergänzt um die eigenen Preise dieses Projekts',
    precastDetail:
      'Betonfertigteile: {volume} m³ Stützen, Binder und Unterzüge der Hallen, mit Bewehrung und Montage',
    replacedPrices:
      'Eigene Preise für Geschossdecken, Betonwände oder Betonstützen gelten nicht mehr: Stahlbeton wird jetzt über Beton, Betonstahl und Schalung berechnet.',
    notes: [
      'Grobe Kostenschätzung aus den Mengen des Plans mit den Einheitspreisen dieses Projekts, netto ohne Mehrwertsteuer.',
      'Nicht ermittelt: Erdarbeiten, Fundamente (sofern nicht aus den Stützen ermittelt), Haustechnik, Außenanlagen, Ausbau und Baunebenkosten, soweit nicht als Zuschlag angesetzt.',
      'Stahlgewicht, Stahlbetonmengen und Fundamente aus der Vorbemessung; ersetzen keine Statik und kein Angebot.',
    ],
  },
  collisions: {
    overlap: '{a} überschneidet {b}',
    wall: '{a} durchdringt eine Wand',
    lining: '{a} ragt in eine Vorsatzschale',
    column: '{a} steht in einer Stütze',
    clearance: 'Freiraum von {a} ist durch {b} belegt',
    clearanceWall: 'Freiraum von {a} reicht in eine Wand',
  },
  stairHints: {
    steep: 'Steigung über 19 cm – Treppe zu steil',
    stepRule: 'Schrittmaß {value} cm außerhalb 59–65 cm',
    narrow: 'Laufbreite unter 1,00 m – als Fluchtweg meist zu schmal',
    landingNeeded:
      '{n} Steigungen in einem Lauf – nach höchstens 18 Steigungen ist ein Zwischenpodest nötig',
  },
  hallStructure: {
    trussGirder:
      'Stahlbinder über {span} m Spannweite: ein Fachwerkbinder ist meist wirtschaftlicher als ein Walzprofil',
    precastSpan:
      'Spannbetonbinder über {span} m sind unüblich – Mittelstützen vorsehen oder in Stahl planen',
    steepPrecast:
      'Dachneigung {pitch}°: Spannbeton-Satteldachbinder haben meist 3–5 % Gefälle; hier steigt die Binderunterseite zum First mit an',
  },
  officeStructure: {
    slabThin:
      '{name}: Decke {thickness} cm ist für {span} m Spannweite zu dünn – empfohlen etwa {recommended} cm',
    wideSpan:
      '{name}: Decken spannen {span} m zwischen tragenden Wänden – Stützen, Unterzug oder tragende Innenwand vorsehen',
    unsupported: '{name}: {n} Stützen im {level} stehen ohne Stütze darunter – Abfangträger nötig',
    hallColumn: '{name}: Hallenstütze in Achse {axis} steht im Bürotrakt',
    hallGableColumn: '{name}: Giebelwandstütze der Halle steht im Bürotrakt',
    headroom: '{name}: reicht bis {top} m, das Hallentragwerk darüber beginnt schon bei {steel} m',
  },
  plan: {
    unassigned: 'Nicht zugeordnet',
    terrace: 'Dachterrasse',
    roof: 'Dach',
    stairFrom: 'ab {level}',
    stairSteps: '{n} Stg. {riser}/{going}',
    stalls: p({ one: '{n} Stellplatz', other: '{n} Stellplätze' }),
    area: 'Bereich {n}',
    stairwell: 'Treppenhaus',
    gfaStorey: 'BGF {level} {area} · gesamt {total}',
    netArea: 'Innen {area}',
    officeTitle: '{name} · {n}-geschossig · {info}',
    floors: p({ one: '{n} Geschoss · {height}', other: '{n} Geschosse · {height}' }),
    levelClearHeight: '{level} · lichte Höhe {height}',
    gfa: 'BGF {area}',
    eave: 'Traufe {height}',
    eaveRidge: 'Traufe {eave} · First {ridge}',
    roomHeight: '{area} · LH {height}',
  },
  errors: {
    hallLocked: 'Die Halle ist gesperrt',
    shellLocked: 'Die Hülle von „{name}“ ist geschützt (erst im Eigenschaftenfenster freigeben)',
    hallCornersMin: 'Eine Halle braucht mindestens drei Ecken',
    mezzanineOutside: 'Die Geschossfläche muss ganz innerhalb der Halle liegen',
    mezzanineHeight:
      'Die Höhe der Geschossfläche muss über der Deckenstärke und mindestens 2 m unter der Traufe liegen',
    mezzanineShape:
      'Die Geschossfläche braucht mindestens drei Ecken und darf sich nicht selbst schneiden',
    mezzanineLocked: 'Die Geschossfläche ist gesperrt',
    mezzanineNoFreeEdge: 'Die Geschossfläche hat keine freie Kante für eine Treppe',
    canopyNoFit: 'Das Vordach ist breiter als die Wand',
    canopyLocked: 'Das Vordach ist gesperrt',
    wallImageLocked: 'Das Wandbild ist gesperrt',
    officeLocked: 'Das Bürogebäude ist gesperrt',
    ownedWall: 'Diese Wand wird über ihre Halle bzw. ihren Raum gesteuert',
    rectRoomsOnly: 'Nur rechteckige Räume sind bearbeitbar',
    noSpace: 'Kein Platz mehr für {type} ({width} m) in dieser Wand',
    rowTooLong: '{n} × {type} passen nicht in die Wand ({total} m benötigt)',
    gateTooWide: 'Der Zaun ist kürzer als das Tor ({width} m)',
    rowOverlap: 'Die Reihe überschneidet vorhandene Öffnungen – bitte Abstand oder Anzahl anpassen',
    stairNeedsOffice: 'Treppen gehören zu einem Bürogebäude',
    storeyOutlineLevel: 'Einen eigenen Umriss gibt es nur für Obergeschosse',
    storeyOutlineOutside:
      'Der Geschossumriss muss auf dem Geschoss darunter liegen und mindestens 4 m² haben',
    noUpperLevel: 'Über diesem Geschoss gibt es kein weiteres',
    stairLocked: 'Die Treppe ist gesperrt',
    lineGone: 'Linie existiert nicht mehr',
    pointGone: 'Punkt existiert nicht',
    segmentGone: 'Abschnitt existiert nicht',
    lineMinPoints: 'Eine Linie braucht mindestens zwei Punkte',
    lineMaxPoints: 'Zu viele Punkte in einer Linie',
    hallGone: 'Halle existiert nicht',
    roofNoFit: 'Keine {type} passt mit diesen Abständen aufs Dach',
    polygonMinPoints: 'Ein Polygon braucht mindestens drei Punkte',
    socialTooSmall: 'Das Geschoss ist zu klein für einen Sozialtrakt (mind. ca. 6 m Länge)',
    templatesOfficeOnly: 'Raumvorlagen gibt es nur für Bürogebäude',
    noInterior: 'Dieses Geschoss hat keinen Innenraum',
    templatesOrthogonal: 'Raumvorlagen gehen nur bei Gebäuden, die rechtwinklig zum Raster stehen',
    levelHasRooms: '{level} hat schon Räume – bitte zuerst löschen',
    refSiteGone: 'Das Bezugsgrundstück existiert nicht mehr',
    siteGone: 'Das Grundstück existiert nicht mehr',
    nothingToTakeOver: 'Die Datei enthält weder Grundstücke noch ein Hintergrundbild',
    siteLocked: 'Das Grundstück ist gesperrt',
    noBackground: 'Es ist kein Hintergrundbild vorhanden',
    backgroundLocked: 'Das Hintergrundbild ist gesperrt',
    lengthPositive: 'Bitte eine Länge größer als 0 angeben',
  },
  /** Names of undoable actions, shown e.g. in „… nicht möglich“ messages. */
  commands: {
    moveSelection: 'Auswahl verschieben',
    rotate: 'Drehen',
    rotateSelection: 'Auswahl drehen',
    removeSelection: 'Auswahl löschen',
    remove: 'Löschen',
    lock: 'Sperren',
    unlock: 'Entsperren',
    protectShell: 'Hülle schützen',
    releaseShell: 'Hülle freigeben',
    alignObjects: 'Objekte ausrichten',
    distributeEven: 'Gleichmäßig verteilen',
    distributeGap: 'Mit festem Abstand verteilen',
    paste: 'Einfügen',
    updateProject: 'Projektdaten ändern',
    updateSettings: 'Einstellungen ändern',
    addElement: 'Element hinzufügen',
    updateElement: 'Element ändern',
    createHall: 'Halle erstellen',
    moveHall: 'Halle verschieben',
    updateHall: 'Halle ändern',
    resizeHall: 'Hallengröße ändern',
    createMezzanine: 'Geschossfläche erstellen',
    restoreWalls: 'Außenwände wiederherstellen',
    createCanopy: 'Vordach erstellen',
    updateCanopy: 'Vordach ändern',
    createWallImage: 'Bild an Wand einfügen',
    updateWallImage: 'Wandbild ändern',
    openWall: 'Wand ganz öffnen',
    createRailing: 'Geländer erstellen',
    updateMezzanine: 'Geschossfläche ändern',
    createOffice: 'Bürogebäude erstellen',
    updateOffice: 'Bürogebäude ändern',
    openWallFloor: 'Außenwand in einem Geschoss öffnen',
    closeWallFloor: 'Außenwand in einem Geschoss schließen',
    setStoreyOutline: 'Geschossumriss zeichnen',
    resetStoreyOutline: 'Geschossumriss zurücksetzen',
    updateFloors: 'Geschosse ändern',
    updateFloorHeight: 'Geschosshöhe ändern',
    drawWall: 'Wand zeichnen',
    moveWall: 'Wand verschieben',
    updateWall: 'Wand ändern',
    rotateWall: 'Wand drehen',
    wallLength: 'Wandlänge ändern',
    createRoom: 'Raum erstellen',
    updateRoom: 'Raum ändern',
    moveRoom: 'Raum verschieben',
    resizeRoom: 'Raumgröße ändern',
    recalcRoomWalls: 'Raumwände neu berechnen',
    roomLayout: 'Raumaufteilung einfügen',
    createZone: 'Zone anlegen',
    updateZone: 'Zone ändern',
    moveZone: 'Zone verschieben',
    resizeZone: 'Zonengröße ändern',
    insertOpening: '{type} einsetzen',
    insertDoubleDock: 'Doppel-Laderampe einsetzen',
    changeOpeningType: 'Öffnungstyp ändern',
    updateOpening: 'Öffnung ändern',
    moveOpening: 'Öffnung verschieben',
    openingWidth: 'Öffnungsbreite ändern',
    openingLevel: 'Geschoss der Öffnung ändern',
    updateDock: 'Laderampe ändern',
    saveMeasurement: 'Maß speichern',
    updateMeasurement: 'Maß ändern',
    moveMeasurement: 'Maß verschieben',
    insertObject: '{name} einfügen',
    updateObject: 'Objekt ändern',
    moveObject: 'Objekt verschieben',
    moveObjects: 'Objekte verschieben',
    hideObjects: 'Objekte ausblenden',
    showObjects: 'Objekte einblenden',
    placeElement: '{type} setzen',
    extendRoad: 'Straße verlängern',
    extendFence: 'Zaun verlängern',
    liftObject: 'Objekt anheben',
    rotateObject: 'Objekt drehen',
    duplicateObject: 'Objekt duplizieren',
    duplicateRoofElements: 'Dachelement duplizieren',
    createColumn: 'Stütze setzen',
    updateColumn: 'Stütze ändern',
    moveColumn: 'Stütze verschieben',
    detachColumns: 'Stützen einzeln bearbeiten',
    restoreColumns: 'Automatische Stützen wiederherstellen',
    resizeObject: 'Größe ändern',
    objectSize: 'Objektgröße ändern',
    updateClearance: 'Freiraum ändern',
    changeLevel: 'Geschoss wechseln',
    updateColor: 'Farbe ändern',
    restoreSize: 'Originalgröße wiederherstellen',
    insertStair: 'Treppe einfügen',
    insertStairWithDoor: 'Treppe mit Podest und Tür einfügen',
    updateStair: 'Treppe ändern',
    moveStair: 'Treppe verschieben',
    rotateStair: 'Treppe drehen',
    updateLanding: 'Podest ändern',
    createOutdoor: '{type} anlegen',
    createFence: 'Zaun anlegen',
    movePoint: 'Punkt verschieben',
    insertPoint: 'Punkt einfügen',
    deletePoint: 'Punkt löschen',
    moveSitePoint: 'Grundstückspunkt verschieben',
    insertSitePoint: 'Grundstückspunkt einfügen',
    deleteSitePoint: 'Grundstückspunkt löschen',
    moveZonePoint: 'Zonenpunkt verschieben',
    insertZonePoint: 'Zonenpunkt einfügen',
    deleteZonePoint: 'Zonenpunkt löschen',
    edgeLength: 'Kantenlänge ändern',
    roadKind: 'Straßenart ändern',
    roadWidth: 'Straßenbreite ändern',
    moveRoad: 'Straße verschieben',
    resizeRoad: 'Straße: Größe ändern',
    fenceHeight: 'Zaunhöhe ändern',
    fenceStyle: 'Zaun ändern',
    addGate: 'Schiebetor einfügen',
    addFenceDoor: 'Durchgangstür einfügen',
    moveGate: 'Schiebetor verschieben',
    updateGate: 'Schiebetor ändern',
    removeGate: 'Schiebetor entfernen',
    moveFence: 'Zaun verschieben',
    resizeFence: 'Zaun: Größe ändern',
    updateParking: 'Parkplatz ändern',
    moveParking: 'Parkplatz verschieben',
    rotateParking: 'Parkplatz drehen',
    resizeParking: 'Parkfläche ändern',
    updateRoofElement: 'Dachelement ändern',
    moveRoofElement: 'Dachelement verschieben',
    removeRoofElements: 'Dachelemente entfernen',
    createSite: 'Grundstück anlegen',
    replaceSite: 'Grundstück ersetzen',
    takeOverSites: 'Grundstück und Hintergrund übernehmen',
    attachSite: 'Grundstück anfügen',
    rotateSite: 'Grundstück drehen',
    updateSite: 'Grundstück ändern',
    renameSite: 'Grundstück umbenennen',
    moveSite: 'Grundstück verschieben',
    siteShape: 'Grundstücksform ändern',
    updateBackground: 'Hintergrundbild ändern',
    insertBackground: 'Hintergrundbild einfügen',
    removeBackground: 'Hintergrundbild entfernen',
    calibrateBackground: 'Maßstab des Hintergrundbilds festlegen',
    rotateBackground: 'Hintergrundbild drehen',
    lockBackground: 'Hintergrundbild sperren',
    unlockBackground: 'Hintergrundbild entsperren',
    alignBackground: 'Hintergrundbild ausrichten',
    moveBackground: 'Hintergrundbild verschieben',
  },
  /** Names given to new elements; numbered names keep their number. */
  defaults: {
    hall: 'Halle {n}',
    office: 'Bürogebäude {n}',
    site: 'Grundstück',
    siteNumbered: 'Grundstück {n}',
    room: 'Raum {n}',
    fence: 'Zaun',
    gate: 'Schiebetor',
    fenceDoor: 'Durchgangstür',
    siteFence: 'Grundstückszaun',
    copy: '{name} (Kopie)',
    officeRoom: 'Büro {n}',
    changingWomen: 'Umkleide Damen',
    changingMen: 'Umkleide Herren',
  },
  parse: {
    notObject: 'Projektdatei ist kein Objekt',
    missingVersion: 'schemaVersion fehlt oder ist ungültig',
    tooNew: 'Projekt wurde mit neuerer Version erstellt (Schema {version}, unterstützt {target})',
    missingMigration: 'Keine Migration von Schema {version}',
    invalidData: 'Projektdaten sind ungültig',
    invalidJson: 'Datei ist kein gültiges JSON',
  },
  issues: {
    idMismatch: 'ID stimmt nicht mit Schlüssel überein',
    idDuplicate: 'ID ist nicht eindeutig',
    siteBoundary: 'Grundstücksgrenze ist kein gültiges Polygon',
    wallTooThick: 'Wandstärke ist zu groß für die Hallenmaße',
    hallOutline: 'Der Gebäudegrundriss darf sich nicht überschneiden und braucht mindestens 4 m²',
    hallOutlineRoof: 'Frei gezeichnete Hallen gibt es nur mit Flach- oder Pultdach',
    mezzanineNoHall: 'Geschossfläche ohne zugehörige Halle',
    columnNoBuilding: 'Stütze ohne zugehörige Halle bzw. ohne passendes Geschoss',
    mezzaninePolygon: 'Ungültiger Umriss einer Geschossfläche',
    mezzanineHeight: 'Höhe der Geschossfläche passt nicht in die Halle',
    canopyWall: 'Vordach ohne zugehörige Wand',
    wallImageWall: 'Wandbild ohne zugehörige Wand',
    canopyPastEnd: 'Vordach ragt über das Wandende hinaus',
    railingRoof: 'Dachgeländer ohne zugehöriges Gebäude',
    officeData: 'Geschossdaten passen nicht zur Gebäudeart',
    officeHeight: 'Gebäudehöhe passt nicht zu den Geschossen',
    stairNoOffice: 'Treppe gehört zu keinem Bürogebäude',
    noUpperLevel: 'Über diesem Geschoss gibt es kein weiteres',
    roofElementNoHall: 'Dachelement gehört zu keiner Halle',
    roofElementOutside: 'Dachelement ragt über die Dachfläche hinaus',
    duplicatePoints: 'Zwei aufeinanderfolgende Punkte sind identisch',
    wallLength: 'Wandlänge muss größer als 0 sein',
    wallOwner: 'Wand verweist auf unbekannte Halle bzw. Raum',
    openingWall: 'Öffnung verweist auf nicht existierende Wand',
    openingPastEnd: 'Öffnung ragt über das Wandende hinaus',
    openingTooHigh: 'Öffnung ist höher als die Wand',
    openingsOverlap: 'Öffnungen in derselben Wand überschneiden sich',
    unknownHall: 'Verweis auf unbekannte Halle',
    zonePolygon: 'Ungültiges Zonen-Polygon',
    roomPolygon: 'Ungültiges Raum-Polygon',
  },
};

export type ModelMessages = typeof model;
